import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { Box, Checkbox, Dialog, HStack, Input, NativeSelect, Portal, Separator, Spinner, Stack, Switch, VStack } from "@chakra-ui/react"
import { Button, Text, toaster } from "@/components"
import { useBulkUpdateDealStage, useDealManagers, useDealPipeline } from "@/hooks"
import { sendIntroEmail } from "@/api"
import { getPortalUrl } from "@/api/deals"
import { useAuth } from "@/contexts"
import { Textarea } from "@chakra-ui/react"
import { FaFire } from "react-icons/fa"
import { LuTimer, LuSend, LuExternalLink } from "react-icons/lu"
// TODO: DealInfo type was removed — no backend route for /deals/pipeline yet
type DealInfo = any

const PORTAL_EMAIL_ADMINS = new Set(["sean@esofund.com", "bill.yu@esofund.com", "jordan@esofund.com"])

const PORTAL_STEP_LABELS: Record<string, string> = {
  complete_nda_form: "NDA Form",
  sign_nda: "Sign NDA",
  upload_grant_docs: "Upload Grants",
  accept_offer: "Accept Offer",
  complete_bc_form: "BC Form",
  sign_bc: "Sign BC",
  upload_closing_docs: "Upload Docs",
  schedule_closing_meeting: "Schedule Closing",
  closing: "Closing",
  sign_contract: "Sign Contract",
  send_wire_info: "Wire Info",
  closed: "Closed",
}

const ITEMS_PER_PAGE = 10

// Bucket definitions - stages that get grouped together
const BUCKET_DEFINITIONS: Record<string, string[]> = {
  "Open": ["NDA Sent", "Open", "NDA Signed"],
  "Ready": ["Ready for Offer"],
  "Rejected": ["Robo Reject", "ESO Passed", "Client Passed"],
  "Offer": ["Offer Sent"],
  "Closing": ["Contract Review", "Exercise Check Sent", "Check 1 Sent", "Check 2 Sent", "Accepted"],
}

// Define the order of buckets (buckets not in this list will appear after in their natural order)
const BUCKET_ORDER = ["Open", "Ready", "Offer", "Closing", "Closed", "Rejected"]

// Stage priority within buckets (higher priority = appears first)
const STAGE_SORT_PRIORITY: Record<string, number> = {
  "Accepted": 1,
  "Offer Sent": 2,
}

// Stages that should be unchecked by default
const UNCHECKED_BY_DEFAULT = new Set(["Rejected", "Closed"])

// Stages that are considered inactive (for active deals KPI)
const INACTIVE_STAGES = new Set(["ESO Passed", "Client Passed", "Robo Reject", "Closed"])

// Stages excluded from "Need Update" bucket
const NEED_UPDATE_EXCLUDED_STAGES = new Set([
  "ESO Passed",
  "Closed",
  "Robo Reject",
  "Client Passed",
  "Exercise Check Sent",
  "Check 1 Sent",
  "Check 2 Sent",
])

// Number of days before a deal is considered needing an update
const NEED_UPDATE_DAYS_THRESHOLD = 30

// Test deal company IDs
const TEST_COMPANY_IDS = new Set([2189, 10612])

// Check if a deal needs an update (not in excluded stages and older than threshold)
const isInSet = (set: Set<string>, value: string): boolean => {
  const lower = value.toLowerCase()
  let found = false
  set.forEach((item) => {
    if (item.toLowerCase() === lower) found = true
  })
  return found
}

const needsUpdate = (deal: DealInfo): boolean => {
  const stageName = deal.stage || ""
  if (isInSet(NEED_UPDATE_EXCLUDED_STAGES, stageName)) {
    return false
  }

  const modifiedDate = new Date(deal.oGLastModDate)
  const thresholdDate = new Date()
  thresholdDate.setDate(thresholdDate.getDate() - NEED_UPDATE_DAYS_THRESHOLD)

  return modifiedDate < thresholdDate
}

// Get the bucket name for a stage, or return the stage name if not in a bucket
const getBucketName = (stageName: string): string => {
  const lower = stageName.toLowerCase()
  for (const [bucketName, stages] of Object.entries(BUCKET_DEFINITIONS)) {
    if (stages.some((s) => s.toLowerCase() === lower)) {
      return bucketName
    }
  }
  return stageName
}

const getExpirationStatus = (expirationDate: string | null | undefined) => {
  if (!expirationDate) return null
  const days = Math.floor((new Date(expirationDate).getTime() - Date.now()) / 86400000)
  if (days < 0) return { days, timerColor: "red.500", badgeColor: "red.500", badge: "Expired", showBadge: true }
  if (days <= 7) return { days, timerColor: "red.500", badgeColor: "red.500", badge: `${days}d left`, showBadge: true }
  if (days <= 30) return { days, timerColor: "orange.500", badgeColor: "orange.500", badge: `${days}d left`, showBadge: true }
  return { days, timerColor: "gray.400", badgeColor: "gray.400", badge: null, showBadge: false }
}

interface KanbanColumnProps {
  bucketName: string
  deals: DealInfo[]
  onDealClick: (dealId: string) => void
  clientNotes: Record<string, string>
  onNotesChange: (dealId: string, notes: string) => void
  selectedDeals: Set<number>
  onToggleDeal: (dealId: number) => void
  onSendIntroEmail: (dealId: number, clientName: string) => void
  sendingDealId: number | null
  canSendPortalEmail: (dealManagerId: number) => boolean
}

const KanbanColumn = ({ bucketName, deals, onDealClick, clientNotes, onNotesChange, selectedDeals, onToggleDeal, onSendIntroEmail, sendingDealId, canSendPortalEmail }: KanbanColumnProps) => {
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Reset visible count when deals change (search, filter, manager change)
  const dealsLength = deals.length
  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE)
  }, [dealsLength])

  const visibleDeals = deals.slice(0, visibleCount)
  const hasMore = visibleCount < deals.length
  const isTerminal = bucketName === "Closed" || bucketName === "Rejected"

  // IntersectionObserver to load more deals when sentinel is visible
  const loadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + ITEMS_PER_PAGE, deals.length))
  }, [deals.length])

  useEffect(() => {
    const sentinel = sentinelRef.current
    const container = scrollContainerRef.current
    if (!sentinel || !container || !hasMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore()
        }
      },
      { root: container, threshold: 0.1 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, loadMore])

  // Colors that work in both light and dark mode
  const stageColors: Record<string, { bg: string; headerBg: string; borderColor: string }> = {
    "Open": { bg: "orange.50/50", headerBg: "orange.100/70", borderColor: "orange.200" },
    "Ready": { bg: "teal.50/50", headerBg: "teal.100/70", borderColor: "teal.200" },
    "Rejected": { bg: "red.50/50", headerBg: "red.100/70", borderColor: "red.200" },
    "Offer": { bg: "yellow.50/50", headerBg: "yellow.100/70", borderColor: "yellow.300" },
    "Closing": { bg: "blue.50/50", headerBg: "blue.100/70", borderColor: "blue.200" },
    "Closed": { bg: "green.50/50", headerBg: "green.100/70", borderColor: "green.200" },
    "default": { bg: "gray.50/50", headerBg: "gray.100/70", borderColor: "gray.200" },
  }

  const colors = stageColors[bucketName] || stageColors["default"]

  return (
    <Box
      minW="340px"
      maxW="340px"
      h="100%"
      bg={colors.bg}
      borderRadius={10}
      border="1px"
      borderColor={colors.borderColor}
      display="flex"
      flexDirection="column"
    >
      {/* Column Header */}
      <Box
        p={3}
        bg={colors.headerBg}
        borderTopRadius={10}
        borderBottom="1px"
        borderColor={colors.borderColor}
      >
        <HStack justifyContent="space-between">
          <Text fontWeight="bold" fontSize="sm" color="black">{bucketName}</Text>
          <Text fontSize="xs" color="text.muted" bg="card.bg" px={2} py={0.5} borderRadius="full">
            {deals.length}
          </Text>
        </HStack>
      </Box>

      {/* Column Body — scrollable */}
      <VStack ref={scrollContainerRef} p={2} gap={2} flex={1} overflowY="auto" alignItems="stretch">
        {visibleDeals.map((deal) => {
          const expStatus = isTerminal ? null : getExpirationStatus(deal.expirationDate)
          const stale = needsUpdate(deal)
          const isUrgent = expStatus?.showBadge && (expStatus.timerColor === "red.500" || expStatus.timerColor === "orange.500")
          return (
          <Box
            key={deal.dealId}
            p={3}
            bg="card.bg"
            borderRadius={8}
            border="1px"
            borderColor={selectedDeals.has(deal.dealId) ? "blue.500" : expStatus?.timerColor === "red.500" && expStatus.showBadge ? "red.400" : expStatus?.timerColor === "orange.500" && expStatus.showBadge ? "orange.400" : "border.muted"}
            boxShadow="sm"
            _hover={{ boxShadow: "md", borderColor: "blue.300" }}
          >
            <HStack alignItems="flex-start" gap={2}>
              <Box pt={0.5} onClick={(e) => e.stopPropagation()}>
                <Checkbox.Root
                  size="sm"
                  checked={selectedDeals.has(deal.dealId)}
                  onCheckedChange={() => onToggleDeal(deal.dealId)}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                </Checkbox.Root>
              </Box>
              <Box flex={1} cursor="pointer" onClick={() => onDealClick(deal.dealId)}>
                {/* Client name + initial payment */}
                <HStack justifyContent="space-between" mb={0.5}>
                  <Text fontWeight="medium" fontSize="sm">
                    {deal.clientFirstName} {deal.clientLastName}
                  </Text>
                  {deal.initialPaymentOptions > 0 && (
                    <Text fontSize="xs" fontWeight="semibold" color="green.600">
                      ${deal.initialPaymentOptions.toLocaleString()}
                    </Text>
                  )}
                </HStack>

                {/* Company name */}
                <HStack gap={1} mb={1}>
                  <Text fontSize="xs" color="text.muted">
                    {deal.companyName || "N/A"}
                  </Text>
                  {deal.isOnHotlist && (
                    <Box as={FaFire as React.ElementType} w="12px" h="12px" color="#f97316" flexShrink={0} />
                  )}
                </HStack>

                {/* Client notes — auto-height based on content */}
                <Textarea
                  size="xs"
                  fontSize="xs"
                  placeholder="Client notes..."
                  value={clientNotes[deal.dealId] ?? deal.clientInfo?.clientNotes ?? ""}
                  onChange={(e) => {
                    e.stopPropagation()
                    onNotesChange(deal.dealId, e.target.value)
                    e.target.style.height = "auto"
                    e.target.style.height = `${e.target.scrollHeight}px`
                  }}
                  onClick={(e) => e.stopPropagation()}
                  ref={(el) => {
                    if (el) {
                      el.style.height = "auto"
                      el.style.height = `${el.scrollHeight}px`
                    }
                  }}
                  mb={1.5}
                  overflow="hidden"
                  resize="none"
                  minH="52px"
                />

                {/* Badge row: DM avatar + stage + portal stage + expiring/expired + stale */}
                <HStack gap={1} flexWrap="wrap" mb={2} alignItems="center">
                  {/* Deal manager avatar */}
                  {deal.dealManagerProfilePicture ? (
                    <Box
                      as="img"
                      src={deal.dealManagerProfilePicture}
                      alt={deal.dealManager || "DM"}
                      w="20px"
                      h="20px"
                      borderRadius="full"
                      objectFit="cover"
                      border="1px"
                      borderColor="border.muted"
                      flexShrink={0}
                    />
                  ) : (
                    <Box
                      w="20px"
                      h="20px"
                      borderRadius="full"
                      bg="gray.300"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                      _dark={{ bg: "gray.600" }}
                    >
                      <Text fontSize="9px" color="gray.600" fontWeight="bold" _dark={{ color: "gray.300" }}>
                        {(deal.dealManager || "?").slice(0, 1).toUpperCase()}
                      </Text>
                    </Box>
                  )}
                  {deal.stage && (
                    <Box
                      display="inline-flex"
                      px={1.5}
                      py={0.5}
                      bg="blue.500/20"
                      borderRadius="full"
                      border="1px"
                      borderColor="blue.500/40"
                    >
                      <Text fontSize="10px" color="blue.fg" fontWeight="medium">
                        {deal.stage}
                      </Text>
                    </Box>
                  )}
                  {deal.introEmailSentAt && deal.portalStage && (
                    <Box
                      display="inline-flex"
                      px={1.5}
                      py={0.5}
                      bg="purple.500/15"
                      borderRadius="full"
                      border="1px"
                      borderColor="purple.500/40"
                    >
                      <Text fontSize="10px" color="purple.600" fontWeight="medium" _dark={{ color: "purple.300" }}>
                        {PORTAL_STEP_LABELS[deal.portalStage] || deal.portalStage}
                      </Text>
                    </Box>
                  )}
                  {expStatus?.showBadge && (
                    <Box
                      display="inline-flex"
                      px={1.5}
                      py={0.5}
                      bg={expStatus.timerColor === "red.500" ? "red.500/15" : "orange.500/15"}
                      borderRadius="full"
                      border="1px"
                      borderColor={expStatus.timerColor === "red.500" ? "red.500/40" : "orange.500/40"}
                    >
                      <Text fontSize="10px" color={expStatus.badgeColor} fontWeight="bold">
                        {expStatus.badge}
                      </Text>
                    </Box>
                  )}
                  {stale && (
                    <Box
                      display="inline-flex"
                      px={1.5}
                      py={0.5}
                      bg="gray.500/15"
                      borderRadius="full"
                      border="1px"
                      borderColor="gray.500/40"
                    >
                      <Text fontSize="10px" color="gray.600" fontWeight="bold" _dark={{ color: "gray.400" }}>
                        STALE
                      </Text>
                    </Box>
                  )}
                </HStack>

                {/* Bottom row: Portal email button (left) + timer (right) */}
                <HStack justifyContent="space-between" alignItems="center">
                  {/* Portal email button or sent status */}
                  <Box>
                    {canSendPortalEmail(deal.dealManagerId) && !deal.introEmailSentAt && (
                      <Box
                        as="button"
                        display="inline-flex"
                        alignItems="center"
                        gap="4px"
                        px={1.5}
                        py={0.5}
                        fontSize="10px"
                        fontWeight="medium"
                        color="purple.600"
                        bg="purple.500/10"
                        borderRadius="full"
                        border="1px"
                        borderColor="purple.500/30"
                        cursor="pointer"
                        _hover={{ bg: "purple.500/20" }}
                        _dark={{ color: "purple.300" }}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation()
                          onSendIntroEmail(deal.dealId, `${deal.clientFirstName} ${deal.clientLastName}`)
                        }}
                        disabled={sendingDealId === deal.dealId}
                        opacity={sendingDealId === deal.dealId ? 0.6 : 1}
                      >
                        <Box as={LuSend as React.ElementType} w="10px" h="10px" flexShrink={0} />
                        {sendingDealId === deal.dealId ? "Sending..." : "Portal Email"}
                      </Box>
                    )}
                    <Box
                      as="button"
                      display="inline-flex"
                      alignItems="center"
                      gap="4px"
                      px={1.5}
                      py={0.5}
                      fontSize="10px"
                      fontWeight="medium"
                      color="green.700"
                      bg="green.500/10"
                      borderRadius="full"
                      border="1px"
                      borderColor="green.500/30"
                      cursor="pointer"
                      _hover={{ bg: "green.500/20" }}
                      _dark={{ color: "green.300" }}
                      onClick={async (e: React.MouseEvent) => {
                        e.stopPropagation()
                        try {
                          const url = await getPortalUrl(deal.dealId)
                          window.open(url, "_blank")
                        } catch (err: any) {
                          const msg = err?.response?.data?.error
                          toaster.error({
                            title: "Could not open portal",
                            description: msg === "Deal not found in legacy database"
                              ? "This deal hasn't been synced to the portal yet"
                              : msg || undefined,
                          })
                        }
                      }}
                    >
                      <Box as={LuExternalLink as React.ElementType} w="10px" h="10px" flexShrink={0} />
                      View in Portal
                    </Box>
                  </Box>

                  {/* Expiration timer */}
                  {expStatus && (
                    <HStack gap={0.5} alignItems="center">
                      <Box as={LuTimer} w="12px" h="12px" color={expStatus.timerColor} flexShrink={0} />
                      <Text fontSize="10px" color={expStatus.timerColor} fontWeight={isUrgent ? "semibold" : "normal"}>
                        {expStatus.days < 0 ? `${Math.abs(expStatus.days)}d ago` : `${expStatus.days}d`}
                      </Text>
                    </HStack>
                  )}
                </HStack>
              </Box>
            </HStack>
          </Box>
          )
        })}

        {/* Sentinel for infinite scroll */}
        {hasMore && (
          <Box ref={sentinelRef} py={2} textAlign="center">
            <Text fontSize="xs" color="text.muted">Loading more...</Text>
          </Box>
        )}

        {visibleDeals.length === 0 && (
          <Box py={4} textAlign="center">
            <Text fontSize="xs" color="text.muted">No deals</Text>
          </Box>
        )}
      </VStack>
    </Box>
  )
}

export const DealPipelineComponent = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [selectedManagerId, setSelectedManagerId] = useState<number | "all" | undefined>(undefined)

  const { data: deals, isLoading } = useDealPipeline(selectedManagerId)

  const canSendPortalEmail = (dealManagerId: number) => {
    if (!user) return false
    const userEmail = (user as any).email as string | undefined
    const userId = (user as any).id as number | undefined
    return PORTAL_EMAIL_ADMINS.has(userEmail ?? "") || userId === dealManagerId
  }
  const { data: managers } = useDealManagers()
  const bulkStageMutation = useBulkUpdateDealStage()
  const [searchQuery, setSearchQuery] = useState("")
  const [hideTestDeals, setHideTestDeals] = useState(true)
  const [visibleBuckets, setVisibleBuckets] = useState<Set<string>>(new Set())
  const [initialized, setInitialized] = useState(false)
  const [clientNotes, setClientNotes] = useState<Record<string, string>>({})
  const [selectedDeals, setSelectedDeals] = useState<Set<number>>(new Set())

  // Portal email confirmation dialog state
  const [confirmDealId, setConfirmDealId] = useState<number | null>(null)
  const [confirmClientName, setConfirmClientName] = useState("")
  const [sendingDealId, setSendingDealId] = useState<number | null>(null)

  // Reset board state when manager filter changes
  useEffect(() => {
    setInitialized(false)
    setSelectedDeals(new Set())
  }, [selectedManagerId])

  const handleNotesChange = (dealId: string, notes: string) => {
    setClientNotes((prev) => ({ ...prev, [dealId]: notes }))
  }

  const handleRequestIntroEmail = (dealId: number, clientName: string) => {
    setConfirmDealId(dealId)
    setConfirmClientName(clientName)
  }

  const handleConfirmIntroEmail = async () => {
    if (!confirmDealId) return
    const dealId = confirmDealId
    setConfirmDealId(null)
    setSendingDealId(dealId)
    try {
      const result = await sendIntroEmail(dealId)
      toaster.success({ title: `Portal email sent to ${result.clientName || confirmClientName}` })
      queryClient.invalidateQueries({ queryKey: ["dealPipeline"] })
    } catch {
      toaster.error({ title: "Failed to send portal email" })
    } finally {
      setSendingDealId(null)
    }
  }

  const handleDealClick = (dealId: string) => {
    navigate(`/core/deal/${dealId}`)
  }

  const handleToggleDeal = (dealId: number) => {
    setSelectedDeals((prev) => {
      const next = new Set(prev)
      if (next.has(dealId)) {
        next.delete(dealId)
      } else {
        next.add(dealId)
      }
      return next
    })
  }

  const handleBulkStageChange = async (stageId: number) => {
    const stageName = stageId === 11 ? "ESO Passed" : "Client Passed"
    const dealIds = Array.from(selectedDeals)

    try {
      const result = await bulkStageMutation.mutateAsync({ dealIds, stageId })
      toaster.success({ title: `${result.count} deal(s) marked as ${stageName}` })
      setSelectedDeals(new Set())
    } catch {
      toaster.error({ title: "Failed to update deal stages" })
    }
  }

  const handleManagerChange = (value: string) => {
    if (value === "me") {
      setSelectedManagerId(undefined)
    } else if (value === "all") {
      setSelectedManagerId("all")
    } else {
      setSelectedManagerId(Number(value))
    }
    setSearchQuery("")
  }

  // Get unique buckets - combining stages into buckets where defined
  const buckets = useMemo(() => {
    // Collect all unique stage names
    const stageNames = new Set<string>()
    deals.forEach((deal: DealInfo) => {
      if (deal.stage) stageNames.add(deal.stage)
    })

    // Collect all unique bucket names from stages
    const seenBuckets = new Set<string>()
    stageNames.forEach((stage) => {
      seenBuckets.add(getBucketName(stage))
    })

    // If any NDA Sent or NDA Signed deals have grants, the "Ready" bucket will appear
    const PRE_READY = new Set(["NDA Sent", "NDA Signed"])
    if (deals.some((d: DealInfo) => PRE_READY.has(d.stage || "") && (d.grantCount ?? 0) > 0)) {
      seenBuckets.add("Ready")
    }

    // Build bucket list following BUCKET_ORDER
    const bucketList: { name: string; priority: number }[] = []
    BUCKET_ORDER.forEach((bucketName, index) => {
      if (seenBuckets.has(bucketName)) {
        bucketList.push({ name: bucketName, priority: index })
        seenBuckets.delete(bucketName)
      }
    })

    // Add any remaining buckets not in BUCKET_ORDER
    seenBuckets.forEach((bucketName) => {
      bucketList.push({ name: bucketName, priority: bucketList.length })
    })

    return bucketList
  }, [deals])

  // Initialize visible buckets when buckets are loaded (exclude Rejected and Closed by default)
  useEffect(() => {
    if (!isLoading && buckets.length > 1 && !initialized) {
      const initialVisible = new Set(
        buckets
          .filter((b) => !UNCHECKED_BY_DEFAULT.has(b.name))
          .map((b) => b.name)
      )
      setVisibleBuckets(initialVisible)
      setInitialized(true)
    }
  }, [buckets, initialized, isLoading])

  // Count active deals (not in inactive stages)
  const activeDealsCount = useMemo(() => {
    return deals.filter((deal: DealInfo) => {
      const stageName = deal.stage || ""
      return !isInSet(INACTIVE_STAGES, stageName)
    }).length
  }, [deals])

  // Filter deals based on test deal toggle and search
  const filteredDeals = useMemo(() => {
    let result = deals as DealInfo[]

    if (hideTestDeals) {
      result = result.filter((d: DealInfo) => !TEST_COMPANY_IDS.has(d.companyId))
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter((deal: DealInfo) => {
        const clientName = `${deal.clientFirstName} ${deal.clientLastName}`.toLowerCase()
        const companyName = (deal.companyName || "").toLowerCase()
        return clientName.includes(query) || companyName.includes(query)
      })
    }

    return result
  }, [deals, searchQuery, hideTestDeals])

  // Group deals by bucket
  const dealsByBucket = useMemo(() => {
    const grouped: Record<string, DealInfo[]> = {}

    buckets.forEach((bucket) => {
      grouped[bucket.name] = []
    })

    filteredDeals.forEach((deal: DealInfo) => {
      const stageName = deal.stage || "Unknown"

      // NDA Sent or NDA Signed with grants (including staged) → "Ready"
      // Stage advances to "Ready for Offer" only after DM confirms staged grants
      const PRE_READY_STAGES = new Set(["NDA Sent", "NDA Signed"])
      let bucketName: string
      if (PRE_READY_STAGES.has(stageName) && (deal.grantCount ?? 0) > 0) {
        bucketName = "Ready"
      } else {
        bucketName = getBucketName(stageName)
      }

      if (!grouped[bucketName]) {
        grouped[bucketName] = []
      }
      grouped[bucketName].push(deal)
    })

    // Sort deals within each bucket
    Object.keys(grouped).forEach((bucketName) => {
      grouped[bucketName].sort((a: DealInfo, b: DealInfo) => {
        // First sort by stage priority if defined (e.g., Accepted before Offer Sent)
        const aPriority = Object.entries(STAGE_SORT_PRIORITY).find(([k]) => k.toLowerCase() === (a.stage || "").toLowerCase())?.[1] ?? 999
        const bPriority = Object.entries(STAGE_SORT_PRIORITY).find(([k]) => k.toLowerCase() === (b.stage || "").toLowerCase())?.[1] ?? 999
        if (aPriority !== bPriority) {
          return aPriority - bPriority
        }
        return new Date(b.oGLastModDate).getTime() - new Date(a.oGLastModDate).getTime()
      })
    })

    return grouped
  }, [filteredDeals, buckets])

  // Reset page when search changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
  }

  const handleBucketToggle = (bucketName: string) => {
    const newVisible = new Set(visibleBuckets)
    if (newVisible.has(bucketName)) {
      newVisible.delete(bucketName)
    } else {
      newVisible.add(bucketName)
    }
    setVisibleBuckets(newVisible)
  }

  const handleSelectAll = () => {
    if (visibleBuckets.size === buckets.length) {
      setVisibleBuckets(new Set())
    } else {
      setVisibleBuckets(new Set(buckets.map((b) => b.name)))
    }
  }

  // Dropdown value: "me" | "all" | "<id>"
  const managerSelectValue =
    selectedManagerId === undefined ? "me" :
    selectedManagerId === "all" ? "all" :
    String(selectedManagerId)

  if (isLoading) {
    return (
      <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
        <Text fontSize="lg" fontWeight="bold" mb={4}>
          Deal Pipeline
        </Text>
        <HStack justifyContent="center" py={10}>
          <Spinner size="lg" color="blue.600" />
        </HStack>
      </Box>
    )
  }

  return (
    <Box
      w="100%"
      minH="calc(100vh - 55px)"
      px={10}
      py={5}
      border="1px"
      borderColor="gray.200"
      borderRadius={10}
      boxShadow="sm"
      bg="card.bg"
      display="flex"
      flexDirection="column"
      overflowY="auto"
    >
      <HStack mb={4} gap={2}>
        <Text fontSize="lg" fontWeight="bold">
          Deal Pipeline
        </Text>
        <Text fontSize="sm" color="text.muted">
          ({activeDealsCount} active)
        </Text>
      </HStack>

      {/* Search, Manager Filter, and Stage Toggles */}
      <Stack mb={4} gap={4} flexShrink={0}>
        <HStack gap={4} flexWrap="wrap">
          <Input
            size="sm"
            w="250px"
            placeholder="Search by client or company..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          <NativeSelect.Root size="sm" w="200px">
            <NativeSelect.Field
              value={managerSelectValue}
              onChange={(e) => handleManagerChange(e.target.value)}
            >
              <option value="me">My Pipeline</option>
              {managers.map((m: any) => (
                <option key={m.id} value={String(m.id)}>
                  {m.name}
                </option>
              ))}
              <option value="all">All Deal Managers</option>
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          <HStack gap={2} alignItems="center">
            <Switch.Root
              size="sm"
              checked={hideTestDeals}
              onCheckedChange={(e) => setHideTestDeals(e.checked)}
            >
              <Switch.HiddenInput />
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
            </Switch.Root>
            <Text fontSize="sm" color="text.muted">Hide test deals</Text>
          </HStack>
          <Text fontSize="sm" color="text.muted">
            {filteredDeals.length} of {deals.length} deals
          </Text>
        </HStack>

        {/* Bucket Visibility Toggles */}
        <HStack gap={4} flexWrap="wrap">
          <Button
            size="xs"
            variant="outline"
            onClick={handleSelectAll}
          >
            {visibleBuckets.size === buckets.length ? "Hide All" : "Show All"}
          </Button>
          {buckets.map((bucket) => (
            <Checkbox.Root
              key={bucket.name}
              checked={visibleBuckets.has(bucket.name)}
              onCheckedChange={() => handleBucketToggle(bucket.name)}
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control />
              <Checkbox.Label>
                <Text fontSize="sm">{bucket.name}</Text>
              </Checkbox.Label>
            </Checkbox.Root>
          ))}
        </HStack>
      </Stack>

      <Separator mb={4} flexShrink={0} />

      {/* Bulk Action Bar */}
      {selectedDeals.size > 0 && (
        <HStack
          mb={4}
          p={3}
          bg="blue.50/50"
          border="1px"
          borderColor="blue.200"
          borderRadius={8}
          flexShrink={0}
          gap={3}
        >
          <Text fontSize="sm" fontWeight="medium">
            {selectedDeals.size} deal(s) selected
          </Text>
          <Button
            size="xs"
            colorPalette="red"
            loading={bulkStageMutation.isPending}
            onClick={() => handleBulkStageChange(11)}
          >
            ESO Passed
          </Button>
          <Button
            size="xs"
            colorPalette="red"
            variant="outline"
            loading={bulkStageMutation.isPending}
            onClick={() => handleBulkStageChange(12)}
          >
            Client Passed
          </Button>
          <Button
            size="xs"
            variant="ghost"
            onClick={() => setSelectedDeals(new Set())}
          >
            Clear
          </Button>
        </HStack>
      )}

      {/* Kanban Board */}
      <Box flex={1} overflowX="auto" overflowY="hidden" pb={2}>
        <HStack gap={4} alignItems="stretch" minW="max-content" h="100%">
          {buckets
            .filter((bucket) => visibleBuckets.has(bucket.name))
            .map((bucket) => (
              <KanbanColumn
                key={bucket.name}
                bucketName={bucket.name}
                deals={dealsByBucket[bucket.name] || []}
                onDealClick={handleDealClick}
                clientNotes={clientNotes}
                onNotesChange={handleNotesChange}
                selectedDeals={selectedDeals}
                onToggleDeal={handleToggleDeal}
                onSendIntroEmail={handleRequestIntroEmail}
                sendingDealId={sendingDealId}
                canSendPortalEmail={canSendPortalEmail}
              />
            ))}
        </HStack>
      </Box>

      {visibleBuckets.size === 0 && (
        <Box py={10} textAlign="center">
          <Text color="text.muted">No buckets selected. Check the boxes above to show deal columns.</Text>
        </Box>
      )}

      {/* Confirmation dialog for sending portal email */}
      <Dialog.Root open={confirmDealId !== null} onOpenChange={(d) => { if (!d.open) setConfirmDealId(null) }}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content maxW="400px">
              <Dialog.Header>
                <Dialog.Title>Send Portal Welcome Email</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <Text fontSize="sm">
                  Send the portal welcome email to <strong>{confirmClientName}</strong>? This will give them access to the client portal.
                </Text>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="outline" size="sm" onClick={() => setConfirmDealId(null)}>
                  Cancel
                </Button>
                <Button colorPalette="purple" size="sm" onClick={handleConfirmIntroEmail}>
                  Send Email
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  )
}
